/**
 * Renderer handlers for the native menu's `shell:` actions (CH-01).
 *
 * These are window/tab operations the command palette has no business owning
 * ("Go to Tab 4" would be noise in a searchable palette), so they live outside
 * the command registry in their own namespace. The keys are typed as
 * `ShellActionId`, so adding an id to `SHELL_ACTION_IDS` without implementing
 * it here is a compile error, and `electron/appMenuTemplate.antiDrift.test.ts`
 * cross-checks both directions at runtime.
 */

import type { ShellActionId } from './appMenuShellActionIds';
import { useWorkspaceStore } from '../../features/workspace';
import { WORKSPACE_CLOSE_ACTIVE_TAB_EVENT } from '../../features/workspace/core/workspaceEvents';
import { useCommandPaletteStore } from '../../features/command-palette';
import { openDataImportForActiveConnection } from '../../features/workspace/data-import/openDataImportForActiveConnection';
import { isBetaFeatureEnabled } from '../../core/beta';

/**
 * Activate the nth workspace tab. Positions address the tab strip left-to-right
 * in store order, so they match what the user sees (note `addTab` prepends, so
 * tab 1 is the newest tab, not the oldest). Slot 9 is the conventional "last
 * tab" rather than literally the ninth — same as Chrome and Safari.
 */
const goToTab = (position: number) => {
  const { tabs, setActiveTab } = useWorkspaceStore.getState();
  if (tabs.length === 0) return;
  const target = position === 9 ? tabs[tabs.length - 1] : tabs[position - 1];
  if (target) setActiveTab(target.id);
};

/**
 * Close the active tab through the tab strip's OWN guarded path.
 *
 * Deliberately NOT `removeTab(activeTabId)`: `workspaceStore.removeTab` skips
 * the commit-or-rollback prompt for an open/errored transaction (which would
 * leak server-side locks), the save/discard prompt for a dirty notebook, and
 * the confirm prompt for a live data-import run. `Tabs.tsx` owns those checks
 * for the X button, and the menu must land in the same place rather than grow
 * a second copy of them. `Tabs.tsx` resolves the active tab and, via
 * `removeTab`, still refuses to close the Start Page.
 */
const closeActiveTab = () => {
  window.dispatchEvent(new CustomEvent(WORKSPACE_CLOSE_ACTIVE_TAB_EVENT));
};

/**
 * Open the Data Import wizard for the connection selected in the Object
 * Explorer — the SAME entry point the Ctrl/Cmd+I shortcut used before this
 * menu existed, so the chord keeps its toast-when-nothing-selected behaviour
 * and its `coerceDatabaseToConnectionLimit` chokepoint. (The palette's
 * `workspace:import-data` command intentionally differs: invoked from a search
 * box there is no implicit selection, so it prompts with the context picker.)
 */
const importData = () => {
  // The menu item is stripped in main when the beta feature is off, but the
  // strip arrives one IPC round-trip after startup — guard the window in
  // between rather than let a Cmd/Ctrl+I open a wizard the user turned off.
  if (!isBetaFeatureEnabled('data-import')) return;
  if (openDataImportForActiveConnection()) {
    window.electronAPI?.analytics
      ?.track('feature:data_import_open', { source: 'menu' })
      .catch(() => {});
  }
};

export const appMenuShellActions: Record<ShellActionId, () => void> = {
  'shell:close-tab': closeActiveTab,
  'shell:command-palette': () => useCommandPaletteStore.getState().toggle(),
  'shell:import-data': importData,
  'shell:go-to-tab-1': () => goToTab(1),
  'shell:go-to-tab-2': () => goToTab(2),
  'shell:go-to-tab-3': () => goToTab(3),
  'shell:go-to-tab-4': () => goToTab(4),
  'shell:go-to-tab-5': () => goToTab(5),
  'shell:go-to-tab-6': () => goToTab(6),
  'shell:go-to-tab-7': () => goToTab(7),
  'shell:go-to-tab-8': () => goToTab(8),
  'shell:go-to-tab-9': () => goToTab(9),
};

/** Run a `shell:` action. Returns false when the id is not a shell action. */
export function runShellAction(id: string): boolean {
  const handler = (appMenuShellActions as Record<string, (() => void) | undefined>)[id];
  if (!handler) return false;
  handler();
  return true;
}
